type WidgetCardState = 'cta' | 'form' | 'success';

type CallbackWidgetOptions = {
    firstDelay?: number;
    repeatDelay?: number;
    autoHideDelay?: number;
    successHideDelay?: number;
    errorHideDelay?: number;
};

type CallbackWidgetApi = {
    open: (card?: WidgetCardState, fromAuto?: boolean) => void;
    close: (scheduleNext?: boolean) => void;
    setCard: (card: WidgetCardState) => void;
    showSuccess: () => void;
    destroy: () => void;
};

declare global {
    interface Window {
        callbackWidgetApi?: CallbackWidgetApi;
        initCallbackWidget?: (
            rootOrSelector: HTMLElement | string,
            options?: CallbackWidgetOptions
        ) => CallbackWidgetApi | null;
        reinitCallbackWidget?: (
            rootOrSelector?: HTMLElement | string,
            options?: CallbackWidgetOptions
        ) => CallbackWidgetApi | null;
    }
}

export function initCallbackWidget(
    rootOrSelector: HTMLElement | string,
    options: CallbackWidgetOptions = {}
): CallbackWidgetApi | null {
    const root =
        typeof rootOrSelector === 'string'
            ? document.querySelector<HTMLElement>(rootOrSelector)
            : rootOrSelector;

    if (!root) return null;

    const panel = root.querySelector<HTMLElement>('.widget-panel');
    const toggleBtn = root.querySelector<HTMLButtonElement>('[data-toggle-widget]');
    const openFormBtn = root.querySelector<HTMLButtonElement>('[data-open-form]');
    const form = root.querySelector<HTMLFormElement>('[data-form]');
    const cards = root.querySelectorAll<HTMLElement>('[data-card]');

    if (!panel || !toggleBtn || !openFormBtn || !form || !cards.length) {
        console.warn('Callback widget: required elements not found');
        return null;
    }

    const FIRST_DELAY = options.firstDelay ?? 2 * 60 * 1000;
    const REPEAT_DELAY = options.repeatDelay ?? 5 * 60 * 1000;
    const AUTO_HIDE_DELAY = options.autoHideDelay ?? 10 * 1000;
    const SUCCESS_HIDE_DELAY = options.successHideDelay ?? 5000;
    const ERROR_HIDE_DELAY = options.errorHideDelay ?? 2000;

    let firstTimer: number | null = null;
    let repeatTimer: number | null = null;
    let autoHideTimer: number | null = null;
    let successTimer: number | null = null;

    let interacted = false;
    let isOpen = false;

    const errorTimers = new WeakMap<HTMLElement, number>();

    const clearTimer = (timer: number | null): null => {
        if (timer !== null) {
            window.clearTimeout(timer);
        }
        return null;
    };

    const clearErrorState = (field: HTMLElement): void => {
        field.classList.remove('is-error');

        const timer = errorTimers.get(field);
        if (typeof timer === 'number') {
            window.clearTimeout(timer);
            errorTimers.delete(field);
        }
    };

    const showFieldError = (field: HTMLElement): void => {
        field.classList.remove('is-error');
        void field.offsetWidth;
        field.classList.add('is-error');

        const prevTimer = errorTimers.get(field);
        if (typeof prevTimer === 'number') {
            window.clearTimeout(prevTimer);
        }

        const nextTimer = window.setTimeout(() => {
            field.classList.remove('is-error');
            errorTimers.delete(field);
        }, ERROR_HIDE_DELAY);

        errorTimers.set(field, nextTimer);
    };

    const setCard = (name: WidgetCardState): void => {
        cards.forEach((card) => {
            card.classList.toggle('is-active', card.dataset.card === name);
        });
    };

    const open = (card: WidgetCardState = 'cta', fromAuto = false): void => {
        isOpen = true;
        root.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        setCard(card);

        autoHideTimer = clearTimer(autoHideTimer);

        if (fromAuto) {
            autoHideTimer = window.setTimeout(() => {
                if (!interacted) {
                    close(true);
                }
            }, AUTO_HIDE_DELAY);
        }
    };

    const close = (scheduleNext = false): void => {
        isOpen = false;
        root.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');

        autoHideTimer = clearTimer(autoHideTimer);
        successTimer = clearTimer(successTimer);

        form.querySelectorAll<HTMLElement>('.form-item.is-error').forEach(clearErrorState);

        if (scheduleNext && !interacted) {
            repeatTimer = clearTimer(repeatTimer);
            repeatTimer = window.setTimeout(() => {
                open('cta', true);
            }, REPEAT_DELAY);
        }
    };

    const showSuccess = (): void => {
        interacted = true;

        firstTimer = clearTimer(firstTimer);
        repeatTimer = clearTimer(repeatTimer);
        autoHideTimer = clearTimer(autoHideTimer);
        successTimer = clearTimer(successTimer);

        isOpen = true;
        root.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        setCard('success');

        successTimer = window.setTimeout(() => {
            close(false);
        }, SUCCESS_HIDE_DELAY);
    };

    const startScenario = (): void => {
        firstTimer = clearTimer(firstTimer);
        repeatTimer = clearTimer(repeatTimer);

        if (interacted) return;

        firstTimer = window.setTimeout(() => {
            open('cta', true);
        }, FIRST_DELAY);
    };

    const onToggleClick = (): void => {
        interacted = true;
        firstTimer = clearTimer(firstTimer);
        repeatTimer = clearTimer(repeatTimer);
        autoHideTimer = clearTimer(autoHideTimer);

        if (isOpen) {
            close(false);
        } else {
            open('cta', false);
        }
    };

    const onOpenFormClick = (): void => {
        interacted = true;
        repeatTimer = clearTimer(repeatTimer);
        autoHideTimer = clearTimer(autoHideTimer);
        setCard('form');
    };

    const onSubmit = (event: SubmitEvent): void => {
        event.preventDefault();

        interacted = true;
        autoHideTimer = clearTimer(autoHideTimer);
        repeatTimer = clearTimer(repeatTimer);

        const nameInput = form.querySelector<HTMLInputElement>('input[type="text"], input[name="name"]');
        const phoneWidget = form.querySelector<HTMLElement>('#phone-widget');
        const phoneInput =
            phoneWidget?.querySelector<HTMLInputElement>('input[type="tel"]') ??
            form.querySelector<HTMLInputElement>('input[type="tel"]');

        const nameField =
            nameInput?.closest<HTMLElement>('.form-item') ?? null;

        const phoneField =
            phoneWidget?.closest<HTMLElement>('.form-item') ??
            phoneInput?.closest<HTMLElement>('.form-item') ??
            null;

        let hasError = false;
        let firstInvalidInput: HTMLInputElement | null = null;

        const nameValue = nameInput?.value.trim() ?? '';
        const phoneValue = phoneInput?.value.trim() ?? '';
        const phoneDigits = phoneValue.replace(/\D/g, '');

        if (!nameValue && nameField && nameInput) {
            showFieldError(nameField);
            hasError = true;
            firstInvalidInput ??= nameInput;
        }

        if ((!phoneValue || phoneDigits.length < 10) && phoneField && phoneInput) {
            showFieldError(phoneField);
            hasError = true;
            firstInvalidInput ??= phoneInput;
        }

        if (hasError) {
            firstInvalidInput?.focus();
            return;
        }

        // Успешную отправку показываем СНАРУЖИ:
        // window.callbackWidget?.showSuccess();
    };

    const onOutsideClick = (event: MouseEvent): void => {
        const target = event.target;

        if (!(target instanceof Node)) return;
        if (!isOpen) return;

        const clickedInsidePanel = panel.contains(target);
        const clickedToggleButton = toggleBtn.contains(target);

        if (!clickedInsidePanel && !clickedToggleButton) {
            close(false);
        }
    };

    const clearErrorOnInput = (event: Event): void => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const field = target.closest<HTMLElement>('.form-item');
        if (!field) return;

        clearErrorState(field);
    };

    toggleBtn.addEventListener('click', onToggleClick);
    openFormBtn.addEventListener('click', onOpenFormClick);
    form.addEventListener('submit', onSubmit);
    form.addEventListener('input', clearErrorOnInput);
    form.addEventListener('change', clearErrorOnInput);
    document.addEventListener('click', onOutsideClick);

    startScenario();

    return {
        open,
        close,
        setCard,
        showSuccess,
        destroy() {
            firstTimer = clearTimer(firstTimer);
            repeatTimer = clearTimer(repeatTimer);
            autoHideTimer = clearTimer(autoHideTimer);
            successTimer = clearTimer(successTimer);

            form.querySelectorAll<HTMLElement>('.form-item.is-error').forEach(clearErrorState);

            toggleBtn.removeEventListener('click', onToggleClick);
            openFormBtn.removeEventListener('click', onOpenFormClick);
            form.removeEventListener('submit', onSubmit);
            form.removeEventListener('input', clearErrorOnInput);
            form.removeEventListener('change', clearErrorOnInput);
            document.removeEventListener('click', onOutsideClick);
        },
    };
}

window.initCallbackWidget = initCallbackWidget;

window.reinitCallbackWidget = (
    rootOrSelector: HTMLElement | string = '#callbackWidget',
    options: CallbackWidgetOptions = {}
): CallbackWidgetApi | null => {
    window.callbackWidgetApi?.destroy();
    window.callbackWidgetApi =
        window.initCallbackWidget?.(rootOrSelector, options) ?? undefined;

    return window.callbackWidgetApi ?? null;
};

document.addEventListener('DOMContentLoaded', () => {
    window.reinitCallbackWidget?.('#callbackWidget', {
        successHideDelay: 5000,
    });
});

//window.callbackWidget?.showSuccess();